import React from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getSavedEmail, setSavedEmail, saveVehicle, unsaveVehicle, checkSaved } from '../lib/watchlist';

/**
 * Heart-a-listing toggle. Prompts for email inline if the user hasn't saved one.
 *  props.vehicleId   — required
 *  props.compact     — if true, tiny icon-only button (marketplace card)
 *  props.dataTestId  — data-testid attribute
 *  props.onChange    — (saved: bool) => void
 */
export default function HeartButton({ vehicleId, compact = false, dataTestId = 'heart-button', onChange }) {
  const [saved, setSaved] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [askEmail, setAskEmail] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');

  React.useEffect(() => {
    const email = getSavedEmail();
    if (!email) return;
    checkSaved(email, vehicleId).then((v) => setSaved(!!v)).catch(() => {});
  }, [vehicleId]);

  const persist = async (email) => {
    setBusy(true);
    try {
      if (saved) {
        await unsaveVehicle(email, vehicleId);
        setSaved(false);
        onChange?.(false);
        toast.success('Removed from your saved list');
      } else {
        await saveVehicle(email, vehicleId);
        setSaved(true);
        onChange?.(true);
        toast.success('Saved · we\u2019ll ping you on price drops');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const email = getSavedEmail();
    if (email) return persist(email);
    setAskEmail(true);
  };

  const confirmEmail = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!/\S+@\S+\.\S+/.test(emailInput)) return toast.error('Enter a valid email');
    setSavedEmail(emailInput);
    setAskEmail(false);
    await persist(emailInput.trim().toLowerCase());
  };

  const btnClass = compact
    ? `w-9 h-9 grid place-items-center rounded-full backdrop-blur-md transition-colors ${
        saved ? 'bg-[#ff2a2a] text-white' : 'bg-black/60 text-white hover:bg-[#ff2a2a]'
      }`
    : `inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
        saved ? 'bg-[#ff2a2a] text-white' : 'hairline text-zinc-300 hover:border-[#ff2a2a] hover:text-white'
      }`;

  return (
    <>
      <button
        onClick={onClick}
        disabled={busy}
        className={btnClass + ' disabled:opacity-60'}
        title={saved ? 'Remove from saved' : 'Save for later'}
        data-testid={dataTestId}
        aria-label={saved ? 'Unsave listing' : 'Save listing'}
        aria-pressed={saved}
      >
        {busy
          ? <Loader2 className={compact ? 'w-4 h-4 animate-spin' : 'w-3.5 h-3.5 animate-spin'} />
          : <Heart className={`${compact ? 'w-4 h-4' : 'w-3.5 h-3.5'} ${saved ? 'fill-current' : ''}`} />
        }
        {!compact && (saved ? 'Saved' : 'Save')}
      </button>

      {askEmail && (
        <div className="fixed inset-0 z-[100] grid place-items-center px-4" data-testid="save-email-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setAskEmail(false)} />
          <div className="relative w-full max-w-sm bg-[#0d0d0d] hairline p-6">
            <div className="inline-flex items-center gap-2 am-pill">
              <Heart className="w-3.5 h-3.5 text-[#ff2a2a]" /> Save for later
            </div>
            <h3 className="mt-3 font-display text-2xl uppercase tracking-tight">One email, no spam.</h3>
            <p className="mt-2 text-sm text-zinc-400">Where should we email you if the seller drops the price?</p>
            <form onSubmit={confirmEmail} className="mt-4 space-y-3">
              <input
                type="email"
                autoFocus
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
                data-testid="save-email-input"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAskEmail(false)}
                  className="hairline hover:border-white/30 text-zinc-300 text-sm font-semibold py-2.5 transition-colors"
                  data-testid="save-email-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold py-2.5 transition-colors"
                  data-testid="save-email-confirm"
                >
                  Save listing
                </button>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 text-center">
                PIPEDA-compliant · unsubscribe anytime
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
